import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, inject, signal, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AdminuiAutocompleteComponent, AutoCompleteSelectEvent } from '../components/forms/adminui-autocomplete/adminui-autocomplete.component';
import { AdminuiAutocompleteGroupDirective } from '../components/forms/adminui-autocomplete/adminui-autocomplete-group.directive';
import { first, firstValueFrom, map } from 'rxjs';
import { validateFrontendTools } from '@visma-swno/gaia-chat-ui';
import type { VsnAuth, NavigationMenu, LanguageCode, TreeLeaf, AppModules, FeedbackConfig } from '@visma-swno/vsn-navigation';
import { AssistantToolsService } from '../assistant/assistant-tools.service';
import { DataDogService } from '../../data-dog.service';
import { AuthService } from '../core/auth/auth.service';
import { ProfileService } from '../core/auth/profile.service';
import { PROFILE_ROLE } from '../core/auth/profile-role';
import { CONTEXT_TYPE } from '../core/auth/context-type';
import { PATH } from '../core/const/path';
import { Context } from '../core/auth/context';
import { ADMIN_UI_TOKEN_KIND, TOKEN_KIND_PROVISIONING } from '../core/auth/storage-keys';
import { DomainService } from '../core/domain/domain.service';
import { WindowRef } from '../core/domain/window-ref';
import { I18NService } from '../core/i18n/i18n.service';
import { NavbarService } from './navbar.service';
import { ContextGroup } from './context-group';
import { AUTOCOMPLETE_MAX_LIMIT } from './autocomplete-limit';
import { LatestExportComponent } from '../exports/latest-export.component';
import { LatestExportsService } from '../exports/latest-exports.service';

// Help-centre URL per locale; anything else gets the language-neutral page. A table rather than the
// three-case switch this replaced, which counted towards initVsnNavigation's complexity.
const HELP_URLS: Readonly<Record<string, string>> = {
    'nb-NO': 'https://docs.vismasoftware.no/no/admin/',
    'sv-SE': 'https://docs.vismasoftware.no/sv/admin/',
};
const HELP_URL_DEFAULT = 'https://docs.vismasoftware.no/admin/';

/** The `tool-call` event's detail, narrowed to what is logged. */
interface ToolCallDetail {
    toolCallId: string;
    toolCallName: string;
    arguments: Record<string, unknown> | string | null;
}

/**
 * The assistant drawer's `aside` is named because it is the second complementary landmark in the
 * shell (the notification drawer is the other), and same-role landmarks need distinguishing names
 * (Web:S5255). That is what the `gaiaAssistantAriaLabel` key is for.
 */
@Component({
    selector: 'adminui-nav-shell',
    imports: [FormsModule, AdminuiAutocompleteComponent, AdminuiAutocompleteGroupDirective, LatestExportComponent],
    templateUrl: './nav-shell.component.html',
    styleUrl: './nav-shell.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class NavShellComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('gaiaChat') gaiaChat?: ElementRef;

    readonly vsnAuth = signal<VsnAuth | undefined>(undefined);
    readonly vsnTree = signal<NavigationMenu | undefined>(undefined);
    readonly vsnLang = signal<LanguageCode>('en');
    readonly vsnHelpUrl = signal<string | undefined>(undefined);
    readonly vsnLandingPageUrl = signal<string | undefined>(undefined);
    readonly vsnModules = signal<AppModules | undefined>(undefined);
    readonly vsnFeedback = signal<FeedbackConfig | undefined>(undefined);

    readonly message = signal<{ [key: string]: string }>({});
    readonly contextGroups = signal<ContextGroup[]>([]);
    readonly selectedContext = signal<Context | undefined>(undefined);

    readonly profileId = signal('');
    readonly baseUrl = signal('');
    readonly lang = signal('');
    readonly isGaiaChatOpen = signal(false);
    readonly isNotificationPanelOpen = signal(false);
    readonly isNotificationActive = signal(false);

    private hostname = ''
    private readonly closeRequestedHandler = () => { this.isGaiaChatOpen.set(false); };
    private readonly authFailedHandler = (event: Event) => { console.error('Gaia auth failed', event); };
    private readonly toolCallHandler = (event: Event) => { this.recordAssistantToolCall(event as CustomEvent<ToolCallDetail>); };
    // The administrator said something, which allows the assistant one selection. `message-sent`
    // fires for a typed message and for a clicked suggested question, and never for the
    // continuation run after a tool call — so this cannot be triggered by the agent itself. The
    // event carries the message text; it is deliberately not read, here or anywhere else.
    private readonly messageSentHandler = () => { this.assistantToolsService.allowSelection(); };
    private logoutClickHandler!: () => void;
    private menuItemChangedHandler!: (e: Event) => void;
    private navigateHandler!: (e: Event) => void;
    private aiAssistantClickHandler!: () => void;
    private notificationPanelHandler!: () => void;

    private readonly authService = inject(AuthService);
    private readonly profileService = inject(ProfileService);
    private readonly navbarService = inject(NavbarService);
    private readonly domainService = inject(DomainService);
    private readonly windowRef = inject(WindowRef);
    private readonly i18nService = inject(I18NService);
    private readonly router = inject(Router);
    private readonly elementRef = inject(ElementRef);
    private readonly latestExportsService = inject(LatestExportsService);
    private readonly assistantToolsService = inject(AssistantToolsService);
    private readonly dataDogService = inject(DataDogService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.hostname = this.windowRef.getHostname();
        this.initVsnNavigation();
        this.initGaia();
        this.loadContextGroups();
        this.setupEventListeners();
        this.elementRef.nativeElement.addEventListener('close-requested', this.closeRequestedHandler);
        this.handleNotificationPanel();
    }

    ngAfterViewInit(): void {
        if (this.gaiaChat) {
            this.gaiaChat.nativeElement.authStrategy = 'visma';
            this.gaiaChat.nativeElement.tokenProvider = () => this.provideGaiaToken();
            // Both are JS-only properties on the element, not attributes — set here rather than
            // bound in the template. `tools` carries the handlers with it; the component strips
            // them and sends only the schemas to the agent.
            this.gaiaChat.nativeElement.hostContextProvider = () => this.assistantToolsService.getHostContext();
            this.gaiaChat.nativeElement.tools = validateFrontendTools(this.assistantToolsService.getTools());
            this.gaiaChat.nativeElement.addEventListener('auth-failed', this.authFailedHandler);
            this.gaiaChat.nativeElement.addEventListener('tool-call', this.toolCallHandler);
            this.gaiaChat.nativeElement.addEventListener('message-sent', this.messageSentHandler);
        }
    }

    ngOnDestroy(): void {
        this.elementRef.nativeElement.removeEventListener('close-requested', this.closeRequestedHandler);
        const hostEl = this.elementRef.nativeElement as HTMLElement;
        hostEl.removeEventListener('vsn-logout-click', this.logoutClickHandler);
        hostEl.removeEventListener('vsn-menu-item-changed', this.menuItemChangedHandler);
        hostEl.removeEventListener('vsn-navigate', this.navigateHandler);
        hostEl.removeEventListener('vsn-ai-assistant-click', this.aiAssistantClickHandler);
        if (this.gaiaChat) {
            this.gaiaChat.nativeElement.removeEventListener('auth-failed', this.authFailedHandler);
            this.gaiaChat.nativeElement.removeEventListener('tool-call', this.toolCallHandler);
            this.gaiaChat.nativeElement.removeEventListener('message-sent', this.messageSentHandler);
        }
        hostEl.removeEventListener('vsn-notification-click', this.notificationPanelHandler);
    }

    onSearchContext(event: any): void {
        const page = 1;
        const limit = AUTOCOMPLETE_MAX_LIMIT;
        if (event.originalEvent.type === 'click') {
            this.authService.getContextResponse(page, limit).pipe(first()).subscribe(data => {
                this.contextGroups.set(this.navbarService.getContextGroups(data.contexts, this.message()));
            });
        } else {
            this.authService.getContextResponse(page, limit, event.query).pipe(first()).subscribe(data => {
                this.contextGroups.set(data ? this.navbarService.getContextGroups(data.contexts, this.message()) : []);
            });
        }
    }

    onSelectContext(event: AutoCompleteSelectEvent): void {
        const context: Context = event.value;
        if (this.profileService.profile.currentContext.contextId !== context.contextId) {
            this.profileService.profile.currentContext = context;
            this.authService.setCurrentContextOnStorage(context);
            if (this.authService.isProvisioningContext(context)) {
                // Provisioning (non-tenant) context: switch to a provisioning token. No AMA patch (endpoint is AMA-only);
                // the context is persisted in session storage above.
                sessionStorage.setItem(ADMIN_UI_TOKEN_KIND, TOKEN_KIND_PROVISIONING);
                this.windowRef.setLocationHref(this.authService.getConnectLoginUrl(this.hostname, { provisioningToken: true }));
            } else {
                // Tenant (AMA) context: the tenant callback persists lastUsedTenantId once the tenant token is issued.
                sessionStorage.removeItem(ADMIN_UI_TOKEN_KIND);
                this.windowRef.setLocationHref(this.authService.getConnectLoginUrl(this.hostname));
            }
        }
    }

    onFocus(event: Event): void {
        (event.currentTarget as HTMLInputElement).select();
    }

    onBlurContext(): void {
        this.selectedContext.set(structuredClone(this.profileService.profile.currentContext));
    }

    /**
     * Which node the navbar tree highlights: the leaf the browser is actually on, and only failing
     * that the one the signed-in roles land on. The URL has to win, or a screen entered by reload or
     * bookmark - which the shell now keeps instead of redirecting - is highlighted as user-access and
     * gets the wrong breadcrumb.
     *
     * Failing a URL match the answer is the FIRST leaf, because `buildTreeSection` pushes them in the
     * same precedence order the landing path uses - user-access, then subscribers, then product. That
     * replaces a role chain that had to be kept in step with the routing by hand, and that answered
     * the section id for an Administrator: right while the Administrator's section had no leaves,
     * wrong now that a Distributor's does. A section with no leaves at all (provisioning, onboarding)
     * is still its own item.
     */
    private currentTreeItem(intermediateId: string, treeLeaves: TreeLeaf[]): string {
        const pathname = this.windowRef.getPathname();
        const leafOnScreen = treeLeaves.find(leaf => leaf.url === pathname);
        if (leafOnScreen) {
            return leafOnScreen.id;
        }
        return treeLeaves[0]?.id ?? intermediateId;
    }

    /**
     * The middle level of the navbar tree - which section the signed-in roles land in, and the leaves
     * under it.
     *
     * Lifted out of initVsnNavigation, which measured a complexity of 22 with this role chain inline.
     * The order of the branches is load-bearing: a BU role wins over subscriber, which wins over
     * company, which wins over administrator.
     */
    private buildTreeSection(
        message: { [key: string]: string },
        rootPath: string,
        labels: { userAccessLabel: string; customersLabel: string; onPremisesIntegrationsLabel: string; productLabel: string }
    ): { intermediateId: string; intermediateLabel: string; treeLeaves: TreeLeaf[] } {
        const profile = this.profileService.profile;
        const roles = profile.roles;
        const isDistributor = profile.currentContext.contextType === CONTEXT_TYPE.distributor;
        const treeLeaves: TreeLeaf[] = [];
        const userAccessLeaf: TreeLeaf = { id: 'user-access', label: labels.userAccessLabel, url: rootPath + '/user-access' };

        // The context type has to open this branch, not the BU roles alone: a Distributor whose only
        // role is Administrator has neither of them, and would otherwise fall through to the
        // Administrator case at the bottom and be offered /provisioning.
        const isBuRole = roles.includes(PROFILE_ROLE.buAccessAdministrator) || roles.includes(PROFILE_ROLE.buSubscriberViewer);
        if (isDistributor || isBuRole) {
            if (roles.includes(PROFILE_ROLE.buAccessAdministrator)) {
                treeLeaves.push(userAccessLeaf);
            }
            if (roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
                treeLeaves.push({ id: 'subscribers', label: labels.customersLabel, url: rootPath + '/subscribers/user-access' });
            }
            // Both conditions, not the role alone: a Customer context carries Administrator too, and
            // the product screen is refused there - offering it would be a menu item that bounces.
            if (isDistributor && roles.includes(PROFILE_ROLE.administrator)) {
                treeLeaves.push({ id: 'product', label: labels.productLabel, url: rootPath + '/product' });
            }
            return { intermediateId: 'business-unit', intermediateLabel: message['labelBusinessUnit'], treeLeaves };
        }

        if (roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
            treeLeaves.push(userAccessLeaf);
            if (this.profileService.onPremiseIntegration) {
                treeLeaves.push({ id: 'onPremisesIntegrations', label: labels.onPremisesIntegrationsLabel, url: rootPath + '/on-premises-integrations' });
            }
            return { intermediateId: 'subscriber', intermediateLabel: message['labelSubscriber'], treeLeaves };
        }

        if (roles.includes(PROFILE_ROLE.companyAdministrator)) {
            treeLeaves.push(userAccessLeaf);
            return { intermediateId: 'company', intermediateLabel: message['labelCompany'], treeLeaves };
        }

        if (roles.includes(PROFILE_ROLE.administrator)) {
            return this.buildAdministratorSection(message, profile.currentContext);
        }

        // No recognised role: the caller previously read `intermediateId` as an unassigned
        // definite-assignment field here, i.e. undefined at runtime. Kept as empty strings so the
        // shape is honest rather than lying about being a string.
        return { intermediateId: '', intermediateLabel: '', treeLeaves };
    }

    /**
     * The section of a context whose role is Administrator and that is not a Distributor (that one is
     * decided above, with the BU roles). Lifted out of buildTreeSection, which the Organization branch
     * took past Sonar's cognitive-complexity limit (S3776).
     */
    private buildAdministratorSection(
        message: { [key: string]: string },
        context: Context
    ): { intermediateId: string; intermediateLabel: string; treeLeaves: TreeLeaf[] } {
        // A pending onboarding task hides everything else: the customer is set up first.
        if (context.taskId) {
            return { intermediateId: 'set_up_your_company', intermediateLabel: message['labelSetUpYourCompany'], treeLeaves: [] };
        }
        // A Kalmar customer's Administrator: Services / Admin / Organization / Company.
        if (context.contextType === CONTEXT_TYPE.customer) {
            const treeLeaves = [{ id: 'organization-company', label: message['menuItemCompany'], url: PATH.context_organization_company }];
            return { intermediateId: 'organization', intermediateLabel: message['labelOrganization'], treeLeaves };
        }
        return { intermediateId: 'provisioning', intermediateLabel: message['labelProvisioning'], treeLeaves: [] };
    }

    private initVsnNavigation(): void {
        const profile = this.profileService.profile;
        const navbarMsg: { [key: string]: string } = this.i18nService.messages['navbar'] || {};

        const message = {
            ...this.i18nService.messages['navbar'],
            ...this.i18nService.messages['help'],
            ...this.i18nService.messages['common']
        };
        this.message.set(message);

        const langCode = profile.locale.split('-')[0];
        const supportedLangs: LanguageCode[] = ['en', 'no', 'nb', 'nn', 'sv', 'nl', 'fi', 'da'];
        this.vsnLang.set(supportedLangs.includes(langCode as LanguageCode) ? langCode as LanguageCode : 'en');

        this.vsnAuth.set({
            user: {
                userId: profile.sub,
                userName: profile.name,
                userEmail: profile.email,
            },
            profilePictureBaseUrl: this.domainService.getVsnProfilePictureBaseUrl(this.hostname),
            myProfileUrl: this.domainService.getVsnMyProfileUrl(this.hostname),
        });

        this.vsnLandingPageUrl.set(this.domainService.getVsnLandingPageUrl(this.hostname));

        this.vsnHelpUrl.set(HELP_URLS[profile.locale] ?? HELP_URL_DEFAULT);

        const roles = profile.roles;
        const rootPath = this.navbarService.getRootContextPath(roles, profile.currentContext.contextType,
            profile.currentContext.taskId);
        const userAccessLabel = navbarMsg['menuItemUserAccess'] || 'User access';
        const customersLabel = navbarMsg['menuItemCustomers'] || 'Customers';
        const onPremisesIntegrationsLabel = navbarMsg['menuItemOnPremisesIntegration'] || 'On-premises integration';
        const productLabel = navbarMsg['menuItemProduct'] || 'Product';

        const { intermediateId, intermediateLabel, treeLeaves } = this.buildTreeSection(
            message, rootPath,
            { userAccessLabel, customersLabel, onPremisesIntegrationsLabel, productLabel }
        );

        const currentAppId = 'Access_Management';
        const currentAppName = 'Admin';

        this.vsnTree.set({
            items: [{
                id: currentAppId,
                label: currentAppName,
                items: [{
                    id: intermediateId,
                    label: intermediateLabel,
                    ...(treeLeaves.length > 0
                        ? { items: treeLeaves as [TreeLeaf, ...TreeLeaf[]] }
                        : { url: rootPath })
                }]
            }],
            currentItem: this.currentTreeItem(intermediateId, treeLeaves),
            search: {
                enabled: true
            }
        });

        this.vsnModules.set({
            activeModuleId: currentAppId,
            activeModuleLabel: currentAppName,
            modules: () => firstValueFrom(
                this.navbarService.getApplicationsVersion2().pipe(
                    map(apps =>
                        apps.map(a => ({
                            // Kept falsy-checking: an application with an empty id must fall back to
                            // its name, otherwise the nav item gets an empty id and cannot be targeted.
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                            id: a.id || a.name.toLowerCase(),  // NOSONAR falsy check: an empty id must fall back to the name
                            label: a.name,
                            url: a.url
                        }))
                    )
                )
            )
        });

        this.vsnFeedback.set(this.domainService.getVsnFeedbackConfig(this.hostname));

        this.selectedContext.set(structuredClone(profile.currentContext));
    }

    private initGaia(): void {
        const profile = this.profileService.profile;
        this.profileId.set('admin-prod');
        this.baseUrl.set(this.domainService.getGaiaChatApiUrl(this.hostname));
        this.lang.set(profile.locale.split('-')[0]);
    }

    private loadContextGroups(): void {
        this.authService.getContextResponse(1, AUTOCOMPLETE_MAX_LIMIT).pipe(first()).subscribe(data => {
            this.contextGroups.set(this.navbarService.getContextGroups(data.contexts, this.message()));
        });
    }

    private setupEventListeners(): void {
        const hostEl = this.elementRef.nativeElement as HTMLElement;

        this.logoutClickHandler = () => { this.router.navigate(['/logout']); };
        this.menuItemChangedHandler = (e: Event) => {
            const leaf = (e as CustomEvent<TreeLeaf>).detail;
            this.router.navigateByUrl(leaf.url);
            const tree = this.vsnTree();
            if (tree) {
                this.vsnTree.set({ ...tree, currentItem: leaf.id });
            }
        };
        this.navigateHandler = (e: Event) => {
            const item = (e as CustomEvent<{ url: string }>).detail;
            this.windowRef.setLocationHref(item.url);
        };
        this.aiAssistantClickHandler = () => {
            this.isNotificationPanelOpen.set(false);
            this.isGaiaChatOpen.update(open => !open);
        };
        this.notificationPanelHandler = () => {
            this.isGaiaChatOpen.set(false);
            this.isNotificationPanelOpen.update(open => !open);
            if (this.isNotificationPanelOpen()) {
                this.latestExportsService.loadLatestExports();
            }
        };

        hostEl.addEventListener('vsn-logout-click', this.logoutClickHandler);
        hostEl.addEventListener('vsn-menu-item-changed', this.menuItemChangedHandler);
        hostEl.addEventListener('vsn-navigate', this.navigateHandler);
        hostEl.addEventListener('vsn-ai-assistant-click', this.aiAssistantClickHandler);
        hostEl.addEventListener('vsn-notification-click', this.notificationPanelHandler);
    }

    /**
     * The token handed to the assistant, refreshed first when it is spent.
     *
     * gaia-chat asks for one on every call it makes and decodes the JWT itself, so refreshing here
     * is enough - there is nothing to push into the component afterwards.
     *
     * It also gives the provider 5 seconds (AuthService.fetchVismaToken in its bundle) and turns a
     * slower answer into an `auth-failed` with reason `token-timeout`. Unlike the onboarding wizard,
     * that needs no recovery here: it never caches a visma token, so the next message calls this
     * again and gets the refreshed one the slow call already stored. Reproduced on stag by holding
     * `POST /connect/token` for 8s: timeout at 5.0s, and the very next message answered normally.
     * A refresh measures ~300-460ms there, so this is the rare case, not the normal one.
     *
     * The stale error gaia leaves on screen afterwards is its own and has no host API - see
     * ODP-43377. Nothing to do here.
     */
    private async provideGaiaToken(): Promise<string | null> {
        const token = await this.authService.getValidAccessToken();
        if (token === null) {
            console.error('Gaia assistant: no valid access token to provide');
        }
        return token;
    }

    /**
     * The event's `arguments`, as an object.
     *
     * The type allows a JSON string, which is the shape a tool call travels in on the wire, so
     * handle it rather than logging three empty arrays and calling the telemetry done. A string
     * that does not parse is not worth failing a log over.
     */
    private toolCallArguments(value: Record<string, unknown> | string | null | undefined): Record<string, unknown> {
        if (typeof value === 'string') {
            try {
                const parsed: unknown = JSON.parse(value);
                return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
            } catch {
                return {};
            }
        }
        return value && typeof value === 'object' ? value : {};
    }

    /**
     * Records that the assistant called one of this app's frontend tools.
     *
     * The change log attributes a grant to the administrator who pressed Grant, and says
     * nothing about how the screen came to be filled in. Without this, an incident review
     * cannot tell an assistant-prepared grant from a hand-made one. The tool itself writes
     * nothing, so this is the only trace there is.
     *
     * Ids and counts only — RUM already carries the signed-in user's id, but the tool result
     * also holds names and email addresses and those do not belong in telemetry. Whitelisted
     * field by field rather than logging `arguments` wholesale, so a tool added later that
     * takes free text cannot start leaking it here.
     */
    private recordAssistantToolCall(event: CustomEvent<ToolCallDetail>): void {
        const detail = event.detail;
        const args = this.toolCallArguments(detail?.arguments);
        const ids = (value: unknown): string[] =>
            Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];

        this.dataDogService.addAction('assistant-tool-call', {
            toolCallId: detail?.toolCallId,
            toolCallName: detail?.toolCallName,
            subjectIds: ids(args['subjectIds']),
            contextIds: ids(args['contextIds']),
            assignmentIds: ids(args['assignmentIds']),
        });
    }

    private handleNotificationPanel(): void {
        this.latestExportsService.openLatestExportsPanel$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                if (value) {
                    this.isGaiaChatOpen.set(false);
                    this.isNotificationPanelOpen.set(true);
                    this.latestExportsService.loadLatestExports();
                } else {
                    this.isNotificationPanelOpen.set(false);
                }
            });

        this.latestExportsService.latestExports$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(latestExports => {
                this.isNotificationActive.set(latestExports.length > 0);
            });

        this.latestExportsService.loadLatestExports();
    }
}
